import React, { createContext, useContext, useEffect, useState } from "react";
import { wsRequest, onBroadcast } from "./wsClient.js";

const THEME_KEY = "589comps_theme";
const ThemeContext = createContext({ theme: "dark", toggleTheme: () => {}, applyAccountTheme: () => {} });

export function useTheme() {
  return useContext(ThemeContext);
}

// The 31 keys a site-wide theme could ever set — kept here (not
// imported from anywhere) since this is the one place in the
// frontend that ever needs the full list, purely to know what to
// clear when an override is removed.
const THEME_VARIABLE_KEYS = [
  "bg-page",
  "bg-panel",
  "bg-panel-alt",
  "bg-input",
  "bg-toolbar",
  "border-color",
  "border-color-light",
  "border-color-subtle",
  "text-primary",
  "text-secondary",
  "text-dim",
  "accent-blue",
  "accent-yellow",
  "accent-green",
  "accent-red",
  "warning-bg",
  "error-bg",
  "toast-success-bg",
  "toast-success-border",
  "toast-success-icon",
  "toast-error-bg",
  "toast-error-border",
  "toast-error-icon",
  "toast-warning-bg",
  "toast-warning-border",
  "toast-warning-icon",
  "toast-info-bg",
  "toast-info-border",
  "toast-info-icon",
  "shadow-color",
  "overlay-color",
];

function clearSiteTheme() {
  const root = document.documentElement;
  for (const key of THEME_VARIABLE_KEYS) root.style.removeProperty(`--${key}`);
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || "dark");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  // The site-wide theme override, if root has set one — loaded once
  // on mount (same siteSettings.get call SiteHeader already makes
  // elsewhere) and refreshed live via the existing
  // site-settings-changed broadcast, rather than a dedicated poll or
  // a new event type.
  useEffect(() => {
    const loadSiteTheme = () => {
      wsRequest("siteSettings.get")
        .then((data) => {
          if (data.activeTheme) {
            const root = document.documentElement;
            for (const [key, value] of Object.entries(data.activeTheme)) {
              root.style.setProperty(`--${key}`, value);
            }
          } else {
            clearSiteTheme();
          }
        })
        .catch(() => {});
    };
    loadSiteTheme();
    return onBroadcast((message) => {
      if (message.type === "site-settings-changed") loadSiteTheme();
    });
  }, []);

  // Called once the logged-in user's own saved preference is known (e.g.
  // from SiteHeader/AdminApp after auth.me resolves) — overrides whatever
  // was in localStorage, since the account-level choice should win once
  // it's actually known. A null/missing account preference (never set)
  // leaves whatever's already active untouched.
  const applyAccountTheme = (accountTheme) => {
    if (accountTheme === "light" || accountTheme === "dark") setTheme(accountTheme);
  };

  // token is optional — pass it when logged in so the choice also
  // persists to the account (follows the user across devices/browsers),
  // not just this browser's localStorage. Works the same for a regular
  // user token or an admin/root one, since they're resolved through the
  // same account model server-side.
  const toggleTheme = (token) => {
    setTheme((t) => {
      const next = t === "dark" ? "light" : "dark";
      if (token) {
        wsRequest("auth.updateMe", { themePreference: next }, token).catch(() => {
          // Not critical if this fails — the toggle itself already
          // worked and saved to localStorage; the account just won't
          // remember it for next time on a different device.
        });
      }
      return next;
    });
  };

  return <ThemeContext.Provider value={{ theme, toggleTheme, applyAccountTheme }}>{children}</ThemeContext.Provider>;
}
